import React, { useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  Pressable,
  FlatList,
  Alert,
  TextInput,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from "react-native";
import { db, auth } from "@/lib/firebase";
import {
  adminListCategories,
  adminListSubcategoriesGlobal,
  adminApproveCategory,
  adminRejectCategory,
  adminApproveSubcategory,
  adminRejectSubcategory,
  adminListTaxonomySuggestions,
  adminListBusinessTaxonomySuggestions,
  adminApproveTaxonomySuggestion,
  adminRejectTaxonomySuggestion,
  adminApproveBusinessTaxonomySuggestion,
  adminRejectBusinessTaxonomySuggestion,
  adminRenameCategory,
  adminRenameSubcategory,
} from "@/lib/taxonomyAdmin";
import { normalizeLabel } from "@/lib/taxonomy";

type Item =
  | ({ kind: "category"; linkedBusinesses?: LinkedBusiness[] } & any)
  | ({ kind: "subcategory"; linkedBusinesses?: LinkedBusiness[] } & any);

type LinkedBusiness = {
  businessId?: string;
  businessName?: string;
  accountNumber?: string;
  ownerId?: string;
  status?: string;
  businessPath?: string;
};

function isSuggestionItem(item: Item) {
  return typeof item?._suggestionPath === "string" && !!item._suggestionPath;
}

function getItemKind(item: any): Item["kind"] {
  return item?.type === "subcategory" || item?.kind === "subcategory"
    ? "subcategory"
    : "category";
}

function getLinkedBusinesses(item: Item): LinkedBusiness[] {
  if (Array.isArray(item.linkedBusinesses)) {
    return (item.linkedBusinesses as LinkedBusiness[]).filter(
      (business) => business?.businessId
    );
  }

  if (typeof item.businessId === "string" && item.businessId.trim()) {
    return [
      {
        businessId: item.businessId,
        businessName: item.businessName,
        accountNumber: item.accountNumber,
        ownerId: item.ownerId,
        status: item.businessStatus,
        businessPath: item._businessPath,
      },
    ];
  }

  return [];
}

function getLinkedBusinessIds(item: Item) {
  return Array.from(
    new Set(
      getLinkedBusinesses(item)
        .map((business) => business.businessId?.trim())
        .filter((id): id is string => !!id)
    )
  );
}

function isBusinessPendingItem(item: Item) {
  return getLinkedBusinesses(item).length > 0;
}

function getSuggestionTypeLabel(item: Item) {
  return item.kind === "category"
    ? "Category suggestion"
    : "Subcategory suggestion";
}

function getSuggestionKey(item: Item) {
  const kind = getItemKind(item);
  const categoryKey = normalizeLabel(
    String(item.categoryId ?? (kind === "category" ? item.id : item.categoryName) ?? "")
  );

  if (kind === "category") {
    return `category:${categoryKey || normalizeLabel(String(item.name ?? ""))}`;
  }

  const subcategoryKey = normalizeLabel(
    String(item.subcategoryId ?? item.id ?? item.name ?? "")
  );

  return `subcategory:${categoryKey}:${subcategoryKey}`;
}

function mergeLinkedBusinesses(
  left: LinkedBusiness[] = [],
  right: LinkedBusiness[] = []
) {
  const map = new Map<string, LinkedBusiness>();

  for (const business of [...left, ...right]) {
    const id = business.businessId?.trim();
    if (!id) continue;
    map.set(id, { ...map.get(id), ...business, businessId: id });
  }

  return Array.from(map.values());
}

function mergePendingItems(items: Item[]) {
  const map = new Map<string, Item>();

  for (const rawItem of items) {
    const item = { ...rawItem, kind: getItemKind(rawItem) } as Item;
    const key = getSuggestionKey(item);
    const existing = map.get(key);

    if (!existing) {
      map.set(key, item);
      continue;
    }

    map.set(key, {
      ...existing,
      ...item,
      kind: existing.kind,
      _suggestionPath: existing._suggestionPath ?? item._suggestionPath,
      _path: existing._suggestionPath ? existing._path : item._path ?? existing._path,
      linkedBusinesses: mergeLinkedBusinesses(
        getLinkedBusinesses(existing),
        getLinkedBusinesses(item)
      ),
    });
  }

  return Array.from(map.values());
}

export default function AdminTaxonomyQueue() {
  const adminUid = auth.currentUser?.uid;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Item[]>([]);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState("");

  async function load() {
    if (!adminUid) {
      setItems([]);
      setLoadError("Sign in as an admin to view pending suggestions.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError("");

    try {
      const errors: string[] = [];
      const readSource = async <T,>(label: string, fn: () => Promise<T[]>) => {
        try {
          return await fn();
        } catch (error: any) {
          errors.push(`${label}: ${error?.message ?? String(error)}`);
          return [];
        }
      };

      const [cats, subs, suggestions, businessSuggestions] = await Promise.all([
        readSource("Pending categories", () =>
          adminListCategories(db, { status: "pending" })
        ),
        readSource("Pending subcategories", () =>
          adminListSubcategoriesGlobal(db, { status: "pending" })
        ),
        readSource("Category suggestions", () =>
          adminListTaxonomySuggestions(db, { status: "pending" })
        ),
        readSource("Business pending taxonomy", () =>
          adminListBusinessTaxonomySuggestions(db)
        ),
      ]);

      const merged = mergePendingItems([
        ...cats.map((c) => ({ ...c, kind: "category" as const })),
        ...subs.map((s) => ({ ...s, kind: "subcategory" as const })),
        ...suggestions.map((s) => ({
          ...s,
          kind:
            s.type === "subcategory"
              ? ("subcategory" as const)
              : ("category" as const),
        })),
        ...businessSuggestions.map((s) => ({
          ...s,
          kind:
            s.type === "subcategory"
              ? ("subcategory" as const)
              : ("category" as const),
        })),
      ]);

      // newest first if you store createdAt; otherwise leave as-is
      merged.sort((a, b) => {
        const ta = a?.createdAt?.seconds ?? 0;
        const tb = b?.createdAt?.seconds ?? 0;
        return tb - ta;
      });

      setItems(merged);
      setLoadError(errors.join("\n"));
    } catch (e: any) {
      setItems([]);
      setLoadError(e?.message ?? "Could not load pending suggestions.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminUid]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((x) => {
      const linkedText = getLinkedBusinesses(x)
        .map((business) =>
          [
            business.businessName,
            business.accountNumber,
            business.ownerId,
            business.businessId,
          ]
            .filter(Boolean)
            .join(" ")
        )
        .join(" ");

      return [
        x?.name,
        x?.categoryName,
        x?.subcategoryName,
        x?.categoryId,
        linkedText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [items, search]);

  function requireCategoryIdForSub(item: Item): string | null {
    if (item.kind !== "subcategory") return null;
    const cid = item?.categoryId;
    return typeof cid === "string" && cid.trim() ? cid : null;
  }

  function promptRename(item: Item) {
    if (isSuggestionItem(item) || isBusinessPendingItem(item)) {
      Alert.alert(
        "Suggestion",
        "Approve or reject this suggestion as submitted. Rename support can be added later."
      );
      return;
    }

    // Alert.prompt doesn't exist on Android
    if (Platform.OS === "android" || !Alert.prompt) {
      Alert.alert(
        "Rename",
        "Alert.prompt isn't available on Android. Use a custom modal for renaming."
      );
      return;
    }

    Alert.prompt(
      "Edit name",
      "Update spelling then approve (recommended).",
      async (text) => {
        const name = (text ?? "").trim();
        if (!name || !adminUid) return;

        try {
          if (item.kind === "category") {
            await adminRenameCategory(db, {
              categoryId: item.id,
              name,
              adminUid,
            });
          } else {
            const categoryId = requireCategoryIdForSub(item);
            if (!categoryId) {
              Alert.alert(
                "Missing categoryId",
                "This subcategory item is missing categoryId. Ensure subcategory docs store categoryId."
              );
              return;
            }

            await adminRenameSubcategory(db, {
              categoryId,
              subcategoryId: item.id,
              name,
              adminUid,
            });
          }

          await load();
        } catch (e: any) {
          Alert.alert("Error", e?.message ?? "Failed to rename.");
        }
      },
      "plain-text",
      item.name
    );
  }

  async function approve(item: Item) {
    if (!adminUid) return;

    try {
      const linkedBusinessIds = getLinkedBusinessIds(item);

      if (item.kind === "category") {
        if (isSuggestionItem(item)) {
          await adminApproveTaxonomySuggestion(db, {
            suggestionPath: item._suggestionPath,
            adminUid,
            linkedBusinessIds,
          });
        } else if (linkedBusinessIds.length > 0) {
          for (const businessId of linkedBusinessIds) {
            await adminApproveBusinessTaxonomySuggestion(db, {
              businessId,
              type: "category",
              adminUid,
              categoryId: item.categoryId ?? item.id,
              categoryName: item.categoryName ?? item.name,
            });
          }
        } else {
          await adminApproveCategory(db, { categoryId: item.id, adminUid });
        }
      } else {
        const categoryId = requireCategoryIdForSub(item);
        if (!categoryId && linkedBusinessIds.length === 0) {
          Alert.alert(
            "Missing categoryId",
            "This subcategory item is missing categoryId. Ensure subcategory docs store categoryId."
          );
          return;
        }

        if (isSuggestionItem(item)) {
          await adminApproveTaxonomySuggestion(db, {
            suggestionPath: item._suggestionPath,
            adminUid,
            linkedBusinessIds,
          });
        } else if (linkedBusinessIds.length > 0) {
          for (const businessId of linkedBusinessIds) {
            await adminApproveBusinessTaxonomySuggestion(db, {
              businessId,
              type: "subcategory",
              adminUid,
              categoryId: categoryId ?? item.categoryId,
              categoryName: item.categoryName,
              subcategoryId: item.subcategoryId ?? item.id,
              subcategoryName: item.subcategoryName ?? item.name,
            });
          }
        } else {
          await adminApproveSubcategory(db, {
            categoryId: categoryId!,
            subcategoryId: item.id,
            adminUid,
          });
        }
      }

      await load();
    } catch (e: any) {
      Alert.alert("Error", e?.message ?? "Approve failed.");
    }
  }

  async function reject(item: Item) {
    if (!adminUid) return;

    Alert.alert("Reject item?", `Reject "${item.name}"?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Reject",
        style: "destructive",
        onPress: async () => {
          try {
            const linkedBusinessIds = getLinkedBusinessIds(item);

            if (item.kind === "category") {
              if (isSuggestionItem(item)) {
                await adminRejectTaxonomySuggestion(db, {
                  suggestionPath: item._suggestionPath,
                  adminUid,
                  linkedBusinessIds,
                });
              } else if (linkedBusinessIds.length > 0) {
                for (const businessId of linkedBusinessIds) {
                  await adminRejectBusinessTaxonomySuggestion(db, {
                    businessId,
                    type: "category",
                    adminUid,
                  });
                }
              } else {
                await adminRejectCategory(db, { categoryId: item.id, adminUid });
              }
            } else {
              const categoryId = requireCategoryIdForSub(item);
              if (!categoryId && linkedBusinessIds.length === 0) {
                Alert.alert(
                  "Missing categoryId",
                  "This subcategory item is missing categoryId. Ensure subcategory docs store categoryId."
                );
                return;
              }

              if (isSuggestionItem(item)) {
                await adminRejectTaxonomySuggestion(db, {
                  suggestionPath: item._suggestionPath,
                  adminUid,
                  linkedBusinessIds,
                });
              } else if (linkedBusinessIds.length > 0) {
                for (const businessId of linkedBusinessIds) {
                  await adminRejectBusinessTaxonomySuggestion(db, {
                    businessId,
                    type: "subcategory",
                    adminUid,
                  });
                }
              } else {
                await adminRejectSubcategory(db, {
                  categoryId: categoryId!,
                  subcategoryId: item.id,
                  adminUid,
                });
              }
            }

            await load();
          } catch (e: any) {
            Alert.alert("Error", e?.message ?? "Reject failed.");
          }
        },
      },
    ]);
  }

  return (
    <View style={styles.container}>
      <Text style={styles.h1}>Pending Suggestions</Text>

      <TextInput
        value={search}
        onChangeText={setSearch}
        placeholder="Search pending…"
        style={styles.input}
        autoCorrect={false}
      />

      {!!loadError ? <Text style={styles.errorText}>{loadError}</Text> : null}

      {loading ? (
        <View style={styles.loadingRow}>
          <ActivityIndicator />
          <Text style={{ opacity: 0.7 }}>Loading…</Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) =>
            `${item.kind}:${
              item._suggestionPath ?? item._businessPath ?? item._path ?? item.id
            }`
          }
          ItemSeparatorComponent={() => <View style={styles.sep} />}
          renderItem={({ item }) => {
            const linkedBusinesses = getLinkedBusinesses(item);
            const primaryBusiness = linkedBusinesses[0];
            const extraBusinessCount = Math.max(linkedBusinesses.length - 1, 0);

            return (
              <View style={styles.card}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.title}>
                    {item.name}{" "}
                    <Text style={styles.badge}>
                      {getSuggestionTypeLabel(item)}
                    </Text>
                  </Text>

                  {item.kind === "subcategory" ? (
                    <Text style={styles.meta}>
                      Category: {item.categoryName || item.categoryId || "-"}
                    </Text>
                  ) : null}

                  {primaryBusiness ? (
                    <View style={styles.businessBlock}>
                      <Text style={styles.meta}>
                        Business:{" "}
                        {primaryBusiness.businessName ||
                          primaryBusiness.businessId ||
                          "-"}
                      </Text>
                      <Text style={styles.meta}>
                        Account No: {primaryBusiness.accountNumber || "-"}
                      </Text>
                      {extraBusinessCount > 0 ? (
                        <Text style={styles.meta}>
                          +{extraBusinessCount} more linked businesses
                        </Text>
                      ) : null}
                    </View>
                  ) : null}

                  <Text style={styles.meta}>
                    CreatedBy: {item.createdByUid ?? item.createdBy ?? "-"}
                  </Text>

                  {isSuggestionItem(item) ? (
                    <Text style={styles.sourceMeta}>Source: suggestion doc</Text>
                  ) : isBusinessPendingItem(item) ? (
                    <Text style={styles.sourceMeta}>
                      Source: business pending taxonomy
                    </Text>
                  ) : null}
                </View>

                <View style={styles.actions}>
                  <Pressable
                    onPress={() => promptRename(item)}
                    style={styles.btn}
                  >
                    <Text style={styles.btnText}>Edit</Text>
                  </Pressable>

                  <Pressable
                    onPress={() => approve(item)}
                    style={[styles.btn, styles.btnGood]}
                  >
                    <Text style={[styles.btnText, styles.btnTextGood]}>
                      Approve
                    </Text>
                  </Pressable>

                  <Pressable
                    onPress={() => reject(item)}
                    style={[styles.btn, styles.btnBad]}
                  >
                    <Text style={[styles.btnText, styles.btnTextBad]}>
                      Reject
                    </Text>
                  </Pressable>
                </View>
              </View>
            );
          }}
          ListEmptyComponent={
            <View style={{ padding: 16 }}>
              <Text style={{ opacity: 0.7 }}>No pending suggestions.</Text>
            </View>
          }
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16, backgroundColor: "#fff" },
  h1: { fontSize: 18, fontWeight: "800", marginBottom: 10 },
  input: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 12,
  },
  loadingRow: { flexDirection: "row", gap: 8, alignItems: "center", padding: 12 },
  errorText: {
    marginBottom: 10,
    color: "#B45309",
    fontSize: 12,
    fontWeight: "800",
  },
  sep: { height: 10 },
  card: {
    borderWidth: 1,
    borderColor: "#eee",
    borderRadius: 14,
    padding: 12,
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
  },
  title: { fontSize: 15, fontWeight: "800" },
  badge: { fontSize: 12, fontWeight: "800", opacity: 0.6 },
  meta: { marginTop: 4, opacity: 0.7, fontSize: 12 },
  businessBlock: {
    marginTop: 6,
  },
  sourceMeta: {
    marginTop: 4,
    opacity: 0.55,
    fontSize: 11,
    fontWeight: "800",
  },
  actions: { gap: 8, alignItems: "flex-end" },
  btn: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
  },
  btnText: { fontWeight: "800" },
  btnGood: { borderColor: "#2e7d32" },
  btnTextGood: { color: "#2e7d32" },
  btnBad: { borderColor: "#c62828" },
  btnTextBad: { color: "#c62828" },
});
